import React, { useEffect, useRef } from 'react';
import { Linkedin } from 'lucide-react';

// Team order is row-major for the 3\u00d73 grid below:
//   row 1: Mike     | Patrick | Alex
//   row 2: Ty       | Chuck   | Angel
//   row 3: Justin   | Leah    | Lyric
// Ty Adams added 2026-05-21 (Chief Financial & Strategy Officer) \u2014 first
// team member on the new public/images/team/ optimized-WebP convention.
// LinkedIn URL is intentionally omitted (TeamCard falls back to "#" via
// `member?.linkedin || '#'`); swap in the real URL when Mike provides it.
const teamMembers = [
  { name: 'Michael Chesbrough', title: 'Co-Founder & Co-CEO',          initials: 'MC', photo: '/assets/images/unnamed_5_-1773084503687.jpg', linkedin: 'https://www.linkedin.com/in/michael-chesbrough-17a0a42b6/', bio: "Leads tech innovation, market expansion, and roofing operations at OpenbooQ." },
  { name: 'Patrick Salyers',    title: 'Co-Founder & Co-CEO',          initials: 'PS', photo: '/assets/images/unnamed_4_-1773084625487.jpg', linkedin: 'http://www.linkedin.com/in/patrick-salyers-4a7052209',     bio: "Drives financial strategy, marketing growth, and revenue across OpenbooQ." },
  { name: 'Alex Skelton',       title: 'Chief Operating Officer',      initials: 'AS', photo: '/assets/images/unnamed_3_-1773084973084.png', linkedin: 'https://www.linkedin.com/in/alex-skelton-8673a43a0/',    bio: "Overseeing daily operations and scaling OpenbooQ\'s service delivery." },
  { name: 'Ty Adams',           title: 'Chief Financial & Strategy Officer', initials: 'TA', photo: '/images/team/ty-adams.webp',           bio: "Leading OpenbooQ's financial strategy and scaling operations across new markets." },
  { name: 'Chuck Gamkong',      title: 'Chief Technology Officer',     initials: 'CG', photo: '/assets/images/unnamed_3_-1773085416010.jpg', bio: "Architecting the AI and technology infrastructure powering SmartPick\u2122." },
  { name: 'Angel Kimble',       title: 'Chief Logistics Officer',      initials: 'AK', photo: '/assets/images/unnamed_2_-1773084995800.png', linkedin: 'https://www.linkedin.com/in/angel-kimble-9b22093b6/',    bio: "Managing logistics and supply chain to ensure seamless installations." },
  { name: 'Justin Fahey',       title: 'Chief Marketing Officer',      initials: 'JF', photo: '/assets/images/unnamed_2_-1773085079525.jpg', linkedin: 'https://www.linkedin.com/in/justinbfahey/',              bio: "Driving brand growth and digital marketing across all OpenbooQ markets." },
  { name: 'Leah Raffles',       title: 'Director of Admin',            initials: 'LR', photo: '/assets/images/unnamed_1_-1773085267031.png', linkedin: 'https://www.linkedin.com/in/leah-raffles-490932222/',    bio: "Keeping OpenbooQ running smoothly behind the scenes every day." },
  { name: 'Lyric Baker',        title: 'Social Media Manager',         initials: 'LB', photo: '/assets/images/unnamed-1773085294840.png',   linkedin: 'https://www.linkedin.com/in/lyric-baker-8222b73b6/',    bio: "Telling the OpenbooQ story across social media and digital channels." },
];

const TeamCard = ({ member, animIndex }) => {
  const cardRef = useRef(null);

  useEffect(() => {
    const el = cardRef?.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
          }, animIndex * 100);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer?.observe(el);
    return () => observer?.disconnect();
  }, [animIndex]);

  return (
    <div
      ref={cardRef}
      className="team-card"
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        boxShadow: '0 4px 20px rgba(0, 1, 80, 0.08)',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease, opacity 0.5s ease',
        opacity: '0',
        transform: 'translateY(20px)',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-6px)';
        e.currentTarget.style.boxShadow = '0 12px 36px rgba(0, 1, 80, 0.16)';
        const bar = e?.currentTarget?.querySelector('.accent-bar');
        if (bar) bar.style.transform = 'scaleX(1)';
        const bio = e?.currentTarget?.querySelector('.hover-bio');
        if (bio) bio.style.opacity = '1';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 1, 80, 0.08)';
        const bar = e?.currentTarget?.querySelector('.accent-bar');
        if (bar) bar.style.transform = 'scaleX(0)';
        const bio = e?.currentTarget?.querySelector('.hover-bio');
        if (bio) bio.style.opacity = '0';
      }}
    >
      {/* Accent bar */}
      <div
        className="accent-bar"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: '#07B0E9',
          transform: 'scaleX(0)',
          transformOrigin: 'left',
          transition: 'transform 0.3s ease',
        }}
      />
      {/* Avatar */}
      <div
        className="flex items-center justify-center mb-4 flex-shrink-0"
        style={{
          width: '88px',
          height: '88px',
          borderRadius: '50%',
          background: member?.photo ? 'transparent' : 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)',
          fontSize: '1.5rem',
          fontWeight: '700',
          color: '#FFFFFF',
          fontFamily: 'Poppins, Inter, sans-serif',
          letterSpacing: '0.05em',
          overflow: 'hidden',
        }}
      >
        {member?.photo ? (
          <img
            src={member?.photo}
            alt={`${member?.name} headshot`}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
          />
        ) : (
          member?.initials
        )}
      </div>
      {/* Name */}
      <h3
        style={{
          color: '#000150',
          fontFamily: 'Poppins, Inter, sans-serif',
          fontWeight: '700',
          fontSize: '1rem',
          lineHeight: '1.3',
          marginBottom: '6px',
        }}
      >
        {member?.name}
      </h3>
      {/* Title */}
      <p
        style={{
          color: '#07B0E9',
          fontFamily: 'Poppins, Inter, sans-serif',
          fontWeight: '500',
          fontSize: '0.875rem',
          lineHeight: '1.4',
          marginBottom: '8px',
          flexGrow: 1,
        }}
      >
        {member?.title}
      </p>
      {/* Hover Bio */}
      <p
        className="hover-bio"
        style={{
          color: '#6B7280',
          fontFamily: 'Poppins, Inter, sans-serif',
          fontWeight: '400',
          fontSize: '0.75rem',
          lineHeight: '1.5',
          marginBottom: '12px',
          opacity: '0',
          transition: 'opacity 0.3s ease',
          minHeight: '2.25rem',
        }}
      >
        {member?.bio}
      </p>
      {/* LinkedIn Icon */}
      <a
        href={member?.linkedin || '#'}
        aria-label={`${member?.name} LinkedIn`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#9CA3AF', transition: 'color 0.2s ease' }}
        onMouseEnter={e => (e.currentTarget.style.color = '#07B0E9')}
        onMouseLeave={e => (e.currentTarget.style.color = '#9CA3AF')}
      >
        <Linkedin size={18} />
      </a>
    </div>
  );
};

const MeetTheTeamSection = () => {
  return (
    <section style={{ background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F8FD 100%)', padding: '80px 0' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: '56px' }}>
          <h2
            style={{
              color: '#000150',
              fontFamily: 'Poppins, Inter, sans-serif',
              fontWeight: '800',
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              lineHeight: '1.2',
              marginBottom: '16px',
            }}
          >
            Meet the Minds Behind OpenbooQ
          </h2>
          <p
            style={{
              color: '#033494',
              fontFamily: 'Poppins, Inter, sans-serif',
              fontWeight: '400',
              fontSize: 'clamp(1rem, 2vw, 1.125rem)',
              lineHeight: '1.6',
              maxWidth: '640px',
              margin: '0 auto',
            }}
          >
            Our team is blending decades of roofing expertise with cutting-edge AI to build a better way to buy a roof.
          </p>
        </div>

        {/* Team Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px',
          }}
          className="team-grid"
        >
          {teamMembers?.map((member, index) => (
            <TeamCard key={index} member={member} animIndex={index} />
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 1024px) {
          .team-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 640px) {
          .team-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default MeetTheTeamSection;
